import { Autocomplete, Button, FormControl, Stack, TextField } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { useCallback } from "react";

import {
  AddRuleModalProps,
  FacilityRuleFields,
  isFacilityEntity,
  isStateEntity,
  isTenantEntity,
  isTenantForStateEntity,
  isTenantRelatedEntity,
  RULE_TYPE_OPTIONS,
  StateRuleFields,
  TenantForStateRuleFields,
  TenantRuleFields,
  useAddRuleForm,
  useCreateRuleMutation,
} from "./addRuleModalComponents";

export function AddRuleModal({ open, onClose, onRuleAdded }: AddRuleModalProps) {
  const alerts = useAlerts();

  const {
    formState,
    entityType,
    stateOptions,
    tenantSearchVariables,
    isValid,
    isStateRevisionAllow,
    reset,
    setSelectedRuleType,
    setValue,
    setNotes,
    setStateInputValue,
    setNeedsCandidate,
    setNotImplemented,
    setTenantForStateValue,
    setSelectedTenant,
    setTenantSearchInput,
    computeFinalValue,
  } = useAddRuleForm();

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const { createRule, loading } = useCreateRuleMutation({
    onSuccess: () => {
      handleClose();
      onRuleAdded();
    },
  });

  const handleSubmit = useCallback(() => {
    if (!formState.selectedRuleType) {
      alerts.error("Please select a rule type");
      return;
    }

    const isTenantRule = isTenantRelatedEntity(formState.selectedRuleType.entity);

    if (isTenantRule && !formState.selectedTenant) {
      alerts.error("Please select a tenant");
      return;
    }

    if (!isTenantRule && !formState.value.trim()) {
      alerts.error("Please enter a value");
      return;
    }

    if (
      isTenantForStateEntity(formState.selectedRuleType.entity) &&
      !formState.tenantForStateValue.trim()
    ) {
      alerts.error("Please select a state");
      return;
    }

    try {
      createRule(formState.selectedRuleType.configType, computeFinalValue(), formState.notes);
    } catch (error) {
      alerts.error(error instanceof Error ? error.message : "Invalid rule configuration");
    }
  }, [formState, computeFinalValue, createRule, alerts]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Add Rule"
      maxWidth="sm"
      fullWidth
      hideCancelButton
      noPrimaryAction
      additionalPrimaryActionElements={[
        <Button key="cancel" variant="outlined" onClick={handleClose}>
          Cancel
        </Button>,
        <Button key="add" variant="contained" onClick={handleSubmit} disabled={loading || !isValid}>
          {loading ? "Adding..." : "Add Rule"}
        </Button>,
      ]}
    >
      <Stack spacing={3}>
        {/* Rule Type Selector */}
        <FormControl fullWidth>
          <Autocomplete
            options={RULE_TYPE_OPTIONS}
            value={formState.selectedRuleType}
            onChange={(_, newValue) => setSelectedRuleType(newValue)}
            groupBy={(option) => (option.action === "allow" ? "Allow" : "Deny")}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField {...params} label="Rule Type" placeholder="Select rule type" />
            )}
          />
        </FormControl>

        {/* Entity-specific fields */}
        {isStateEntity(entityType) && (
          <StateRuleFields
            value={formState.value}
            inputValue={formState.stateInputValue}
            stateOptions={stateOptions}
            onInputChange={setStateInputValue}
            onChange={setValue}
            isStateRevisionAllow={isStateRevisionAllow}
            needsCandidate={formState.needsCandidate}
            notImplemented={formState.notImplemented}
            onNeedsCandidateChange={setNeedsCandidate}
            onNotImplementedChange={setNotImplemented}
          />
        )}

        {isFacilityEntity(entityType) && (
          <FacilityRuleFields value={formState.value} onChange={setValue} />
        )}

        {isTenantEntity(entityType) && (
          <TenantRuleFields
            selectedTenant={formState.selectedTenant}
            tenantSearchVariables={tenantSearchVariables}
            onTenantChange={setSelectedTenant}
            onInputChange={setTenantSearchInput}
          />
        )}

        {isTenantForStateEntity(entityType) && (
          <TenantForStateRuleFields
            selectedTenant={formState.selectedTenant}
            tenantSearchVariables={tenantSearchVariables}
            tenantForStateValue={formState.tenantForStateValue}
            stateOptions={stateOptions}
            onTenantChange={setSelectedTenant}
            onTenantInputChange={setTenantSearchInput}
            onStateChange={setTenantForStateValue}
          />
        )}

        {/* Notes field */}
        <TextField
          fullWidth
          label="Notes (Optional)"
          value={formState.notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={3}
          placeholder="Add any notes about this rule"
        />
      </Stack>
    </Dialog>
  );
}
